import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import BackBar from "../components/BackBar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Gift, CheckCircle2, Sparkles } from "lucide-react";
import Brand from "../components/Brand";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intendedPlan = params.get("plan");
  const isFree = intendedPlan === "free";
  // ref_code: prefer URL ?ref= (Pricing → Signup), fall back to localStorage (Landing/?ref= → SiteNav → Signup)
  const autoRefCode = (params.get("ref") || (typeof window !== "undefined" && localStorage.getItem("faitharama_ref")) || "").trim().toUpperCase() || null;
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [manualRef, setManualRef] = useState("");  // "Who told you about us?" optional field
  const [loading, setLoading] = useState(false);

  // Effective ref code: auto-capture wins, but if the visitor TYPES a name/code
  // in the manual field we honour that. If both are empty we send null and the
  // signup ends up "unattributed" → Mike sees it in his admin panel and assigns
  // credit manually.
  const refCode = (manualRef.trim() || autoRefCode || "").toUpperCase() || null;

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (form.password.length < 6) throw { response: { data: { detail: "Password must be at least 6 characters." } } };
      // Pass plan: 'free' for forever-free tier, otherwise default trial
      await signup({ ...form, plan: isFree ? "free" : "trial", ref_code: refCode });
      // Clear the ref code so subsequent fresh signups in same browser aren't attributed to the same affiliate
      try { localStorage.removeItem("faitharama_ref"); } catch {}
      toast.success(isFree ? "Welcome to your free Faitharama membership!" : "Welcome to Faitharama All Access!");
      // If a paid plan was selected on Pricing, send straight to Stripe checkout
      if (intendedPlan && !isFree) {
        try {
          const r = await api.post("/subscription/checkout", { plan_id: intendedPlan, origin_url: window.location.origin });
          window.location.href = r.data.url;
          return;
        } catch (err) {
          toast.error("Account created, but checkout couldn't start. Try the Pricing page again.");
        }
      }
      nav("/dashboard");
    } catch (err) { toast.error(err.response?.data?.detail || "Signup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <SiteNav />
      <BackBar />
      <div className="max-w-2xl mx-auto px-6 py-14">
        {isFree ? (
          <>
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 bg-white border border-[#F39A1F] rounded-full px-4 py-1.5">
                <Gift className="w-4 h-4 text-red-wine"/>
                <span className="text-[10px] tracking-wider-cap font-bold text-red-wine">★ 7-DAY FREE TRIAL · NO CREDIT CARD ★</span>
              </div>
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl text-red-wine text-center uppercase leading-[1.05]">
              Start Your 7-Day Free Trial
            </h1>
            <div className="divider-gold"></div>
            <p className="text-center text-foreground text-lg font-semibold max-w-xl mx-auto">
              Create your Faitharama account in 30 seconds. <strong className="text-red-wine">Free for 7 days.</strong> No card. Cancel anytime.
            </p>

            {/* CONCRETE TRIAL BREAKDOWN — what they get even if they never touch a tool */}
            <div className="mt-8 max-w-3xl mx-auto">
              <div className="text-center text-[11px] tracking-wider-cap font-bold text-gold-deep mb-3">★ EXACTLY What's In Your Trial Account ★</div>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Passive perks — get these without using a single tool */}
                <div className="bg-white rounded-xl border-2 border-[#F39A1F] p-5">
                  <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-2">★ Even If You Never Build Anything</div>
                  <h3 className="font-serif-display text-2xl text-red-wine uppercase mb-3">Free To Receive</h3>
                  <ul className="space-y-2 text-base text-foreground">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>Free Guide</strong> — scrollable digital read</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>Browse all 30 funnel templates</strong> (Faith + Wellness)</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>Browse the Academy</strong> — preview every course before joining</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>Browse + buy Christian Store</strong> — supplements, merch, books</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>Read the Community board</strong> — encouragement, prayer requests, posts</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>1 free course</strong> + downloadable certificate when complete</span></li>
                  </ul>
                </div>

                {/* Active earner perks — affiliate-led income */}
                <div className="bg-gradient-to-br from-white to-white rounded-xl border-2 border-red-wine p-5">
                  <div className="text-[10px] tracking-wider-cap font-bold text-red-wine mb-2">★ Even If You Never Pay Us A Dime</div>
                  <h3 className="font-serif-display text-2xl text-red-wine uppercase mb-3">Free To Earn</h3>
                  <ul className="space-y-2 text-base text-foreground">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Your own affiliate link</strong> — <em><Brand suffix=".com"/>/?ref=YOUR-CODE</em></span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Flat 50% commission — same for trial members and paid members</strong> — on every signup, course, supplement &amp; book</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Upgrade to Pro ($49/mo) or Pro ($79/mo)</strong> → 50% commission flat (no hoops)</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Keep 100%</strong> on your own products you sell off-platform</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Move up to Gold (50%)</strong> at $1,000 in sales</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Live click + signup tracker</strong> in your back office</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-red-wine flex-shrink-0 mt-0.5"/><span><strong>Keep your affiliate link forever</strong> — even if you upgrade or downgrade</span></li>
                  </ul>
                </div>

                {/* If they DO want to use tools */}
                <div className="bg-white rounded-xl border border-border p-5 md:col-span-2">
                  <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-2">★ If You Want To Build</div>
                  <h3 className="font-serif-display text-2xl text-red-wine uppercase mb-3">Free To Use Our Tools (Lite)</h3>
                  <div className="grid md:grid-cols-3 gap-3 text-base text-foreground">
                    <div className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>1 funnel max</strong> · use any of 30 templates · publish to <Brand suffix=".com"/>/f/yours</span></div>
                    <div className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>50 contacts</strong> on your email list with CSV export</span></div>
                    <div className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep flex-shrink-0 mt-0.5"/><span><strong>Capture leads</strong> on your funnel, hosted forever</span></div>
                  </div>
                </div>

                {/* What's NOT included so it's not vague */}
                <div className="bg-foreground/5 rounded-xl border border-border p-5 md:col-span-2">
                  <div className="text-[10px] tracking-wider-cap font-bold text-foreground mb-2">★ Not Included (Upgrade to Faithful $49 to Unlock)</div>
                  <div className="grid md:grid-cols-3 gap-2 text-sm text-foreground">
                    <div>✗ Email broadcasts to your list</div>
                    <div>✗ 10,000 contacts (vs 50)</div>
                    <div>✗ Unlimited funnels (vs 1)</div>
                    <div>✗ Sell your own products (when launched)</div>
                    <div>✗ Issue your own diplomas/certificates</div>
                    <div>✗ Tag-based email automation</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl md:text-5xl text-red-wine text-center uppercase">Start All Access · $49/mo</h1>
            <div className="divider-gold"></div>
            <p className="text-center text-foreground font-semibold">Everything <Brand/> offers — one simple price. Cancel anytime.</p>
            {intendedPlan && !isFree && (
              <p className="text-center mt-3 text-sm text-[#0F172A] bg-white border border-[#F39A1F]/50 rounded-md p-3" data-testid="signup-intent-banner">
                After you create your account we'll send you straight to checkout for the <strong className="text-red-wine uppercase">{intendedPlan.replace("_", " ")}</strong> plan.
              </p>
            )}
          </>
        )}

        <form onSubmit={submit} className={`faith-card p-8 mt-7 space-y-5 ${isFree ? "border-2 border-[#F39A1F] shadow-2xl" : ""}`} data-testid="signup-form">
          {isFree && (
            <div className="text-center mb-2">
              <Sparkles className="w-6 h-6 text-gold-deep inline-block"/>
              <div className="text-xs tracking-wider-cap font-bold text-gold-deep uppercase mt-1">7-Day Free Trial</div>
            </div>
          )}
          <div>
            <label className="text-sm font-bold text-foreground">Full name</label>
            <input data-testid="signup-name" required placeholder="Your full name" className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border bg-white focus:border-[#F39A1F] focus:outline-none" value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-bold text-foreground">Email</label>
            <input data-testid="signup-email" required type="email" placeholder="your@email.com" className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border bg-white focus:border-[#F39A1F] focus:outline-none" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-bold text-foreground">Password <span className="text-foreground/70 font-normal text-xs">(at least 6 characters)</span></label>
            <input data-testid="signup-password" required type="password" minLength={6} placeholder="Pick something you'll remember" className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border bg-white focus:border-[#F39A1F] focus:outline-none" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} />
          </div>

          {/* Who told you about Faitharama? — optional referral attribution
              field. If a `?ref=CODE` arrived on the URL we hide this and treat
              it as already-attributed. If not, the new member can simply type
              the person's name or affiliate code and Michael will manually
              assign the credit from his Admin → Recent Signups panel. */}
          {!autoRefCode && (
            <div className="rounded-xl p-4 bg-white border-2 border-[#F39A1F]/40" data-testid="signup-manual-ref">
              <label className="text-sm font-bold text-foreground">
                Who told you about <Brand suffix=".com"/>? <span className="text-foreground/60 font-normal text-xs">(optional)</span>
              </label>
              <input
                data-testid="signup-ref-input"
                placeholder="Their name or referral code"
                className="w-full mt-1 px-4 py-3 rounded-md border-2 border-border bg-white focus:border-[#F39A1F] focus:outline-none"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value)}
                autoComplete="off"
              />
              <p className="text-[11px] italic mt-1.5 text-foreground/70">
                Nobody specific? Leave it blank. Michael personally makes sure the right person gets credited.
              </p>
            </div>
          )}

          {autoRefCode && (
            <div className="rounded-xl p-3 bg-[#E8F5DD] border border-[#2E9F3A]/50 text-sm text-center" data-testid="signup-ref-captured">
              ★ Joining through <strong className="text-[#1F8A2A]">{autoRefCode}</strong> — they&apos;ll be credited for your signup.
            </div>
          )}

          <button data-testid="signup-submit" disabled={loading} className={`w-full py-4 rounded-md font-bold uppercase tracking-wider-cap text-base transition ${isFree ? "bg-[#F39A1F] text-[#0F172A] hover:bg-[#F39A1F]/90 shadow-xl" : "bg-red-wine text-white hover:bg-red-wine/90"}`}>
            {loading ? "Creating your account..." : (isFree ? "★ START MY 7-DAY FREE TRIAL ★" : (intendedPlan ? "Continue to Checkout" : "Start All Access · $49/mo"))}
          </button>
          <div className="text-center text-sm text-foreground">Already a member? <Link to={`/login${intendedPlan ? `?plan=${intendedPlan}` : ""}`} className="text-red-wine font-bold">Sign in</Link></div>
        </form>

        {!isFree && (
          <p className="text-center mt-5 text-sm text-foreground">
            Don't want to commit yet? <Link to="/signup?plan=free" className="text-red-wine font-bold underline">Start a 7-day free trial instead →</Link>
          </p>
        )}
      </div>
    </div>
  );
}
